import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertTriangle, Building2, Loader2, Pencil, Plus, UserCheck } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { paraBooleano } from '@/lib/utils';
import type { RespostaPaginada } from '@/components/paginacao';
import type { Ubs, Usuario } from '@/features/admin/tipos';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { BarraBusca } from '@/components/barra-busca';
import { Paginacao } from '@/components/paginacao';
import { Botao } from '@/components/ui/button';
import { Entrada, Selecao } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Etiqueta } from '@/components/ui/badge';
import {
  Tabela,
  TabelaCabecalho,
  TabelaCelula,
  TabelaCorpo,
  TabelaLinha,
  TabelaTitulo,
  TabelaVazia,
} from '@/components/ui/table';
import { EsqueletoTabela } from '@/components/ui/skeleton';
import {
  Dialogo,
  DialogoCabecalho,
  DialogoConteudo,
  DialogoCorpo,
  DialogoDescricao,
  DialogoRodape,
  DialogoTitulo,
} from '@/components/ui/dialog';

const esquema = z.object({
  nome: z.string().min(3, 'Informe o nome da UBS (mínimo 3 caracteres).'),
  email: z.string().min(1, 'Informe o e-mail institucional.').email('Informe um e-mail válido.'),
  cnes: z
    .string()
    .regex(/^\d{7}$/, 'O CNES deve ter exatamente 7 dígitos.')
    .or(z.literal(''))
    .optional(),
  telefone: z.string().optional(),
  endereco: z.string().optional(),
  reguladoresIds: z.array(z.string()),
  ativa: z.boolean().optional(),
});

type Formulario = z.infer<typeof esquema>;

const COLUNAS = 6;

export function PaginaUbs() {
  const queryClient = useQueryClient();
  const [pagina, setPagina] = React.useState(1);
  const [busca, setBusca] = React.useState('');
  const [emEdicao, setEmEdicao] = React.useState<Ubs | null>(null);
  const [dialogoAberto, setDialogoAberto] = React.useState(false);

  const parametros = new URLSearchParams({ pagina: String(pagina), limite: '20' });
  if (busca) parametros.set('busca', busca);

  const { data, isPending } = useQuery({
    queryKey: ['ubs', pagina, busca],
    queryFn: () => api.get<RespostaPaginada<Ubs>>(`/ubs?${parametros.toString()}`),
  });

  const { data: reguladores } = useQuery({
    queryKey: ['usuarios-regulacao'],
    queryFn: () =>
      api.get<RespostaPaginada<Usuario>>('/usuarios?perfil=REGULACAO&ativo=true&limite=100'),
    staleTime: 5 * 60_000,
  });

  const formulario = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: {
      nome: '',
      email: '',
      cnes: '',
      telefone: '',
      endereco: '',
      reguladoresIds: [],
    },
  });

  const abrirCriacao = () => {
    setEmEdicao(null);
    formulario.reset({
      nome: '',
      email: '',
      cnes: '',
      telefone: '',
      endereco: '',
      reguladoresIds: [],
    });
    setDialogoAberto(true);
  };

  const abrirEdicao = (ubs: Ubs) => {
    setEmEdicao(ubs);
    formulario.reset({
      nome: ubs.nome,
      email: ubs.email,
      cnes: ubs.cnes ?? '',
      telefone: ubs.telefone ?? '',
      endereco: ubs.endereco ?? '',
      reguladoresIds: ubs.reguladores.map((regulador) => regulador.id),
      ativa: ubs.ativa,
    });
    setDialogoAberto(true);
  };

  const salvar = useMutation({
    mutationFn: (dados: Formulario) => {
      // Campos opcionais vazios não devem virar string vazia no banco.
      const corpo = {
        ...dados,
        cnes: dados.cnes || undefined,
        telefone: dados.telefone || undefined,
        endereco: dados.endereco || undefined,
        // Lista vazia significa "sem responsável" e é enviada assim: na
        // edição, ela substitui os responsáveis atuais.
        reguladoresIds: dados.reguladoresIds,
      };

      return emEdicao
        ? api.patch<Ubs>(`/ubs/${emEdicao.id}`, corpo)
        : api.post<Ubs>('/ubs', { ...corpo, ativa: undefined });
    },
    onSuccess: async () => {
      toast.success(emEdicao ? 'UBS atualizada.' : 'UBS cadastrada.');
      setDialogoAberto(false);
      await queryClient.invalidateQueries({ queryKey: ['ubs'] });
    },
    onError: (erro) => {
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível salvar.');
    },
  });

  const aoEnviar = formulario.handleSubmit((dados) => salvar.mutate(dados));

  return (
    <>
      <CabecalhoPagina
        titulo="Unidades Básicas de Saúde"
        descricao="O e-mail institucional recebe a cópia das notificações de agendamento da unidade."
        acoes={
          <Botao onClick={abrirCriacao}>
            <Plus aria-hidden="true" />
            Nova UBS
          </Botao>
        }
      />

      <div className="mb-3">
        <BarraBusca
          valor={busca}
          aoMudar={(valor) => {
            setBusca(valor);
            setPagina(1);
          }}
          placeholder="Buscar por nome, CNES ou e-mail"
        />
      </div>

      <Tabela>
        <TabelaCabecalho>
          <TabelaLinha>
            <TabelaTitulo>Unidade</TabelaTitulo>
            <TabelaTitulo>CNES</TabelaTitulo>
            <TabelaTitulo>Regulador responsável</TabelaTitulo>
            <TabelaTitulo>Usuários</TabelaTitulo>
            <TabelaTitulo>Situação</TabelaTitulo>
            <TabelaTitulo className="text-right">Ações</TabelaTitulo>
          </TabelaLinha>
        </TabelaCabecalho>
        <TabelaCorpo>
          {isPending && <EsqueletoTabela colunas={COLUNAS} />}

          {!isPending && data?.dados.length === 0 && (
            <TabelaVazia colunas={COLUNAS}>
              {busca ? 'Nenhuma UBS encontrada para esta busca.' : 'Nenhuma UBS cadastrada ainda.'}
            </TabelaVazia>
          )}

          {data?.dados.map((ubs) => (
            <TabelaLinha key={ubs.id}>
              <TabelaCelula rotulo="Unidade">
                <div className="flex items-center gap-2">
                  <Building2 className="text-silenciado-texto size-4 shrink-0" aria-hidden="true" />
                  <span className="font-medium">{ubs.nome}</span>
                </div>
              </TabelaCelula>
              <TabelaCelula rotulo="CNES" className="text-silenciado-texto tabular-nums">
                {ubs.cnes ?? '—'}
              </TabelaCelula>
              <TabelaCelula rotulo="Reguladores responsáveis">
                {ubs.reguladores.length > 0 ? (
                  <ul className="flex flex-col gap-0.5">
                    {ubs.reguladores.map((regulador) => (
                      <li key={regulador.id} className="flex items-center gap-1.5 text-sm">
                        <UserCheck className="text-sucesso size-3.5 shrink-0" aria-hidden="true" />
                        {regulador.nome}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span
                    className="text-alerta flex items-center gap-1.5 text-sm"
                    title="Sem responsável designado, nenhum regulador consegue decidir sobre as solicitações desta unidade."
                  >
                    <AlertTriangle className="size-3.5 shrink-0" aria-hidden="true" />
                    Não designado
                  </span>
                )}
              </TabelaCelula>
              <TabelaCelula rotulo="Usuários" className="tabular-nums">
                {ubs._count?.usuarios ?? 0}
              </TabelaCelula>
              <TabelaCelula rotulo="Situação">
                <Etiqueta tom={ubs.ativa ? 'sucesso' : 'neutro'}>
                  {ubs.ativa ? 'Ativa' : 'Inativa'}
                </Etiqueta>
              </TabelaCelula>
              <TabelaCelula className="text-right">
                <Botao
                  variante="fantasma"
                  tamanho="sm"
                  onClick={() => abrirEdicao(ubs)}
                  aria-label={`Editar ${ubs.nome}`}
                >
                  <Pencil aria-hidden="true" />
                  Editar
                </Botao>
              </TabelaCelula>
            </TabelaLinha>
          ))}
        </TabelaCorpo>
      </Tabela>

      {data && <Paginacao meta={data.meta} aoMudarPagina={setPagina} />}

      <Dialogo open={dialogoAberto} onOpenChange={setDialogoAberto}>
        <DialogoConteudo>
          <DialogoCabecalho>
            <DialogoTitulo>{emEdicao ? 'Editar UBS' : 'Nova UBS'}</DialogoTitulo>
            <DialogoDescricao>
              {emEdicao
                ? 'Alterações passam a valer imediatamente para as notificações da unidade.'
                : 'Cadastre a unidade que encaminhará solicitações à Regulação.'}
            </DialogoDescricao>
          </DialogoCabecalho>

          <form onSubmit={(evento) => void aoEnviar(evento)} noValidate>
            <DialogoCorpo>
              <Campo
                id="ubs-nome"
                rotulo="Nome da unidade"
                obrigatorio
                erro={formulario.formState.errors.nome?.message}
              >
                <Entrada autoFocus {...formulario.register('nome')} />
              </Campo>

              <Campo
                id="ubs-email"
                rotulo="E-mail institucional"
                obrigatorio
                ajuda="Recebe a cópia das notificações de agendamento dos pacientes da unidade."
                erro={formulario.formState.errors.email?.message}
              >
                <Entrada type="email" {...formulario.register('email')} />
              </Campo>

              <div className="grid gap-4 sm:grid-cols-2">
                <Campo
                  id="ubs-cnes"
                  rotulo="CNES"
                  ajuda="7 dígitos"
                  erro={formulario.formState.errors.cnes?.message}
                >
                  <Entrada inputMode="numeric" maxLength={7} {...formulario.register('cnes')} />
                </Campo>

                <Campo id="ubs-telefone" rotulo="Telefone">
                  <Entrada {...formulario.register('telefone')} />
                </Campo>
              </div>

              <Campo id="ubs-endereco" rotulo="Endereço">
                <Entrada {...formulario.register('endereco')} />
              </Campo>

              <Campo
                id="ubs-reguladores"
                rotulo="Reguladores responsáveis"
                ajuda="Só eles registram decisões sobre as solicitações da unidade; qualquer um dos marcados pode decidir. Sem responsável, ninguém regula."
                erro={formulario.formState.errors.reguladoresIds?.message}
              >
                <Controller
                  control={formulario.control}
                  name="reguladoresIds"
                  render={({ field }) => (
                    <ul className="border-borda max-h-48 overflow-y-auto rounded-md border">
                      {reguladores?.dados.length === 0 && (
                        <li className="text-silenciado-texto p-3 text-sm">
                          Nenhum usuário ativo com perfil de Regulação.
                        </li>
                      )}
                      {reguladores?.dados.map((regulador) => {
                        const marcado = field.value.includes(regulador.id);
                        return (
                          <li key={regulador.id} className="border-borda border-b last:border-b-0">
                            <label className="hover:bg-silenciado flex cursor-pointer items-center gap-2 p-2 text-sm">
                              <input
                                type="checkbox"
                                className="accent-marca size-4"
                                checked={marcado}
                                onChange={() =>
                                  field.onChange(
                                    marcado
                                      ? field.value.filter((id) => id !== regulador.id)
                                      : [...field.value, regulador.id],
                                  )
                                }
                              />
                              {regulador.nome}
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                />
              </Campo>

              {emEdicao && (
                <Campo
                  id="ubs-ativa"
                  rotulo="Situação"
                  ajuda="Inativar impede novos acessos e novas solicitações da unidade. O histórico é preservado."
                >
                  <Selecao
                    {...formulario.register('ativa', {
                      setValueAs: paraBooleano,
                    })}
                  >
                    <option value="true">Ativa</option>
                    <option value="false">Inativa</option>
                  </Selecao>
                </Campo>
              )}
            </DialogoCorpo>

            <DialogoRodape>
              <Botao variante="contorno" onClick={() => setDialogoAberto(false)}>
                Cancelar
              </Botao>
              <Botao type="submit" disabled={salvar.isPending}>
                {salvar.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
                Salvar
              </Botao>
            </DialogoRodape>
          </form>
        </DialogoConteudo>
      </Dialogo>
    </>
  );
}
